import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { MapPin, ArrowUpRight } from "lucide-react";
import { DC_AREAS, MD_AREAS, MEDIA } from "../lib/constants";
import { NEIGHBORHOODS } from "../lib/neighborhoods";

export default function AreasServed() {
  return (
    <>
      <SEO
        title="Areas Served | District Wellness Center | Cannabis Delivery DC 20011 & Citywide"
        description="Same-day medical cannabis delivery from 4915 Georgia Ave NW (20011) across DC — Petworth, Brightwood, 16th St Heights, Park View, Columbia Heights, Shaw, Dupont, Georgetown, Capitol Hill, Navy Yard."
        path="/areas-served"
        breadcrumbs={[{ name: "Areas Served", path: "/areas-served" }]}
      />

      <section className="bg-ink text-white relative" data-testid="areas-hero">
        <div className="grain absolute inset-0 opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">Coverage · District + MD</div>
            <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight">
              Delivering <em className="text-brand">premium cannabis</em><br/>across D.C. & Maryland.
            </h1>
            <p className="mt-6 text-white/70 max-w-xl text-lg leading-relaxed">
              From Petworth porches to Dupont high-rises — DWC delivers same-day across every corner of
              the District (and select Maryland neighborhoods).
            </p>
          </div>
          <div className="lg:col-span-5">
            <img src={MEDIA.wallGallery} alt="Cannabis product display case and wall gallery at District Wellness Center" className="w-full h-72 lg:h-96 object-cover border border-white/10" />
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-4">Washington, D.C.</div>
              <h2 className="font-serif text-4xl sm:text-5xl leading-[1] tracking-tight">DC neighborhoods we serve.</h2>
            </div>
            <div className="text-sm text-ink-muted">{DC_AREAS.length} neighborhoods · same-day delivery</div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DC_AREAS.map((a, i) => {
              const hood = NEIGHBORHOODS[a.slug] || Object.values(NEIGHBORHOODS).find((h) => h.slug.includes(a.slug));
              const Wrapper = hood ? Link : "article";
              const wrapperProps = hood
                ? { to: `/${hood.slug}`, "data-testid": `dc-area-${a.slug}` }
                : { "data-testid": `dc-area-${a.slug}` };
              return (
                <Wrapper
                  key={a.slug}
                  {...wrapperProps}
                  id={a.slug}
                  className={`p-7 border border-black/10 hover:border-brand hover:bg-[#FAFAFA] transition-colors group ${hood ? "block" : ""}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-ink-muted group-hover:text-brand">Area 0{i + 1}</div>
                    {hood ? <ArrowUpRight size={16} className="text-brand" /> : <MapPin size={16} className="text-brand" />}
                  </div>
                  <h3 className="font-serif text-2xl leading-tight mb-3">{a.name}</h3>
                  <p className="text-base text-ink-muted leading-relaxed">{a.blurb}</p>
                  {hood && (
                    <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-brand group-hover:underline">
                      View {a.name} dispensary page →
                    </div>
                  )}
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-4">Maryland</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1] tracking-tight">Select MD areas.</h2>
            <p className="mt-4 text-ink-muted max-w-xl">For MD patients — check with our team on same-day eligibility and current delivery windows.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {MD_AREAS.map((a) => (
              <div key={a.slug} className="p-6 border border-black/10 bg-white font-serif text-xl" data-testid={`md-area-card-${a.slug}`}>
                {a.name}
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em]" data-testid="areas-contact-btn">
              Don't see your neighborhood? Ask us →
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
